import { useEffect, useMemo } from 'react'
import type { DocumentRecord, ScreenshotRecord, SidebarTab } from '../types'

interface SidebarPanelProps {
  activeDocumentId: string | null
  activeTab: SidebarTab
  documents: DocumentRecord[]
  isOpen: boolean
  screenshots: ScreenshotRecord[]
  onClose: () => void
  onDocumentOpen: (documentId: string) => Promise<void>
  onDeleteScreenshot: (screenshotId: string) => Promise<void>
  onScreenshotOpen: (screenshot: ScreenshotRecord) => void
  onTabChange: (tab: SidebarTab) => void
}

export function SidebarPanel({
  activeDocumentId,
  activeTab,
  documents,
  isOpen,
  screenshots,
  onClose,
  onDocumentOpen,
  onDeleteScreenshot,
  onScreenshotOpen,
  onTabChange,
}: SidebarPanelProps) {
  const objectUrls = useMemo(() => {
    return Object.fromEntries(
      screenshots.map((screenshot) => [screenshot.id, URL.createObjectURL(screenshot.blob)]),
    )
  }, [screenshots])

  useEffect(() => {
    return () => {
      Object.values(objectUrls).forEach((url) => URL.revokeObjectURL(url))
    }
  }, [objectUrls])

  return (
    <>
      <div
        className={`sidebar-backdrop ${isOpen ? 'is-open' : ''}`}
        role="presentation"
        onClick={onClose}
      />
      <aside className={`sidebar-shell ${isOpen ? 'is-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-tabs">
            <button
              type="button"
              className={`ghost-button ${activeTab === 'history' ? 'is-active' : ''}`}
              onClick={() => onTabChange('history')}
            >
              History
            </button>
            <button
              type="button"
              className={`ghost-button ${activeTab === 'saved' ? 'is-active' : ''}`}
              onClick={() => onTabChange('saved')}
            >
              Saved
            </button>
          </div>
          <button type="button" className="ghost-button sidebar-close" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="sidebar-list">
          {activeTab === 'history' ? (
            documents.length === 0 ? (
              <p className="sidebar-empty">Previously opened files will appear here.</p>
            ) : (
              documents.map((documentRecord) => (
                <article
                  key={documentRecord.id}
                  className={`sidebar-card ${activeDocumentId === documentRecord.id ? 'is-active' : ''}`}
                >
                  <button
                    type="button"
                    className="sidebar-card-button"
                    onClick={() => void onDocumentOpen(documentRecord.id)}
                  >
                    <p>{documentRecord.name}</p>
                    <span>
                      {new Date(documentRecord.updatedAt).toLocaleString()}
                      {activeDocumentId === documentRecord.id ? ' · Open now' : ''}
                    </span>
                  </button>
                </article>
              ))
            )
          ) : (
            screenshots.length === 0 ? (
              <p className="sidebar-empty">Saved screens will appear here.</p>
            ) : (
              screenshots.map((screenshot) => (
                <article key={screenshot.id} className="sidebar-card image-card">
                  {objectUrls[screenshot.id] ? (
                    <button
                      type="button"
                      className="screenshot-thumb"
                      onClick={() => onScreenshotOpen(screenshot)}
                    >
                      <img
                        src={objectUrls[screenshot.id]}
                        alt={`Saved screen from ${new Date(screenshot.createdAt).toLocaleString()}`}
                      />
                    </button>
                  ) : null}
                  <div className="sidebar-card-meta">
                    <p>{new Date(screenshot.createdAt).toLocaleString()}</p>
                    <span>
                      {Math.round(screenshot.width)} x {Math.round(screenshot.height)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => void onDeleteScreenshot(screenshot.id)}
                  >
                    Remove
                  </button>
                </article>
              ))
            )
          )}
        </div>
      </aside>
    </>
  )
}
